import { ICellRendererAngularComp } from '@ag-grid-community/angular';
import { ColDef, ICellRendererParams } from '@ag-grid-community/core';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { SessionParams } from '@frontend-site/shared/data';
import { IconName } from '@panaya/ui/p-icon';
import { LinkTarget } from '@panaya/ui/p-link';

@Component({
    selector: 'entity-routing-link-renderer',
    template: `
        <span *ngIf="!value || isDisabled" [attr.automation-id]="automationId">
            {{ value }}
        </span>
        <p-icon *ngIf="icon" [name]="icon" size="xs" [attr.role]="roleName"> </p-icon>
        <p-icon-image *ngIf="iconImage" [name]="iconImage" size="xs" [attr.role]="roleName"> </p-icon-image>
        <!-- This will be relevant when the angular ui will not implement as iframe -->
        <!--<a
            *ngIf="value && linkData && !insideIframe"
            class="grid-link p-paragraph-2 p-tpgr-link "
            [attr.automation-id]="automationId"
            [routerLink]="linkData?.toString() + itemId?.toString()"
            [queryParams]="queryParams"
            (click)="clickEvent($event)"
        >
            {{ value }}
        </a>-->

        <a
            *ngIf="value && (url || linkData) && !isDisabled"
            class="grid-link p-paragraph-2 p-tpgr-link "
            [attr.automation-id]="automationId"
            [href]="url | params: urlArgs"
            [target]="target"
            (click)="clickEvent($event)"
        >
            {{ value }}
        </a>
    `,
    styleUrls: ['./routing-link-renderer.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class RoutingLinkRendererComponent implements ICellRendererAngularComp {
    appId!: number;
    // itemId: number; // This will be relevant when the angular ui will not implement as iframe
    field: string | undefined;
    linkData!: any[];
    url!: string;
    urlArgs!: string[];
    insideIframe!: boolean;
    value: any;
    queryParams!: Params;
    automationId!: string;
    target!: LinkTarget;
    navigateAtOldSite: boolean = false;
    oldSiteStateParam!: string;
    sessionParams!: SessionParams | null;
    isDisabled = false;
    roleName!: string;
    icon: IconName | null = null;
    iconImage: IconName | null = null;
    afterClickEvent!: () => void;
    clickEvent = (event: MouseEvent): void => {
        const shouldOpenInNewTab = event.ctrlKey;
        if (shouldOpenInNewTab) {
            return;
        }
        if (this.insideIframe && this.linkData) {
            event.preventDefault();
            event.stopPropagation();

            const { queryParams, urlArgs, linkData, sessionParams } = this;
            if (sessionParams) {
                sessionStorage.setItem(sessionParams.key, JSON.stringify(sessionParams.params));
            }
            this.router.navigate([linkData.toString(), ...urlArgs], { relativeTo: this.route, queryParams });

            if (this.afterClickEvent) {
                this.afterClickEvent();
            }
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        let url = (event.target as HTMLAnchorElement)?.href || this.url;
        if (this.target === '_blank') {
            window.open(url);
        } else {
            url = url.replace(location.origin.replace(':4300', ''), '').replace('/site/rdx', '').replace(':4300', '');
            this.router.navigateByUrl(url);
        }

        if (this.afterClickEvent) {
            this.afterClickEvent();
        }

        return;
    };

    agInit(params: ICellRendererParams): void {
        const colDef = params.colDef as ColDef;
        const data = colDef.cellRendererParams;
        const {
            insideIframe,
            url,
            linkData,
            idProp,
            isDisabled,
            urlArgs,
            queryParams,
            clickEvent,
            afterClickEvent,
            sessionParamsHandler,
            icon,
            iconImage,
            role
        } = data || {};
        this.insideIframe = insideIframe;
        this.url = !!url && typeof url === 'function' ? url(params.node.data) : url;
        this.linkData = !!linkData && typeof linkData === 'function' ? linkData(params.node.data) : linkData;
        this.queryParams = !!queryParams && typeof queryParams === 'function' ? queryParams(params.node.data) : queryParams;
        this.value = params.getValue?.();
        this.isDisabled =
            !!isDisabled && typeof isDisabled === 'function' ? isDisabled(params.node.data) : isDisabled || params.value === 'None';
        this.field = colDef.field;
        this.appId = params.node.data?.appId;
        // this.itemId = idProp ? params.data[idProp] : this.value;
        this.urlArgs =
            !!urlArgs && typeof urlArgs === 'function'
                ? urlArgs(params.node.data)
                : urlArgs && urlArgs.length
                ? urlArgs.map((arg: string) => params.data[arg]?.toString())
                : [this.value];
        this.automationId = 'routing_link_renderer-app_' + this.appId + '-field_' + this.field;
        this.target = data?.target ?? '_self';
        this.afterClickEvent = afterClickEvent;
        this.clickEvent = clickEvent || this.clickEvent;
        if (data.navigateAtOldSite) {
            this.navigateAtOldSite = data.navigateAtOldSite;
        }
        this.oldSiteStateParam = data.oldSiteStateParam;
        this.sessionParams = sessionParamsHandler ? sessionParamsHandler(params.node.data) : null;

        const readIcon = (iconPropName: IconName | ((data: any) => IconName)): IconName | null => {
            return typeof iconPropName === 'function' ? iconPropName(params.node.data) : iconPropName ?? null;
        };
        this.icon = readIcon(icon);
        this.iconImage = readIcon(iconImage);
        this.roleName = role ?? '';
    }

    constructor(private router: Router, private route: ActivatedRoute) {}

    refresh(_params: ICellRendererParams): boolean {
        return false;
    }
}
